// Orders page › order detail: the sales channel's OWN sales invoices and
// credit notes for this order (BargainFox today; Portal-BE
// GET /orders/:order_id/channel-documents). The channel generates each PDF
// on request and Portal proxies it without storing it, so a click can take a
// moment and can fail when the channel doesn't answer.
//
// Switched on per channel (Integrations › the channel's card › API,
// channelDocuments). Renders nothing when the order's channel doesn't offer
// documents or the switch is off, when there is nothing to list, and when
// the lookup is refused or fails: the panel is context, never a reason for
// the order modal to show an error.
//
// A consolidated Portal order can span several channel orders; the
// documents are then grouped under each channel order number (#364931).
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FileText } from "lucide-react";
import { useTranslation } from "../../i18n";
import { openProtectedFile, gbp } from "../../api/returns";
import {
    ChannelDocument, ChannelDocumentsResponse, channelDocumentsForOrder, channelDocumentPdfPath,
} from "../../api/channelDocuments";
import "../../styles/admin-returns.css";

/** Pence in, "£12.34" out; another currency keeps its code ("12.34 EUR"). */
const money = (minor: number | null, currency: string | null): string | null => {
    if (minor === null) return null;
    const code = (currency || "GBP").toUpperCase();
    if (code === "GBP") return gbp(minor);
    return `${(minor / 100).toFixed(2)} ${code}`;
};

const timeOf = (iso: string | null) => {
    const n = iso ? new Date(iso).getTime() : NaN;
    return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
};

interface Group { reference: string; documents: ChannelDocument[] }

/** Documents by channel order, in the order the API first names each; invoices before credit notes, oldest first. */
const groupDocuments = (documents: ChannelDocument[]): Group[] => {
    const groups: Group[] = [];
    const byRef = new Map<string, Group>();
    for (const d of documents) {
        let g = byRef.get(d.order_reference);
        if (!g) {
            g = { reference: d.order_reference, documents: [] };
            byRef.set(d.order_reference, g);
            groups.push(g);
        }
        g.documents.push(d);
    }
    for (const g of groups) {
        g.documents = g.documents
            .map((d, i) => ({ d, i }))
            .sort((a, b) => (a.d.type === b.d.type ? 0 : a.d.type === "invoice" ? -1 : 1)
                || timeOf(a.d.issued_at) - timeOf(b.d.issued_at)
                || a.i - b.i)
            .map(({ d }) => d);
    }
    return groups;
};

const OrderChannelDocuments = ({ orderId }: { orderId: string }) => {
    const { t, language } = useTranslation();
    const [data, setData] = useState<ChannelDocumentsResponse | null>(null);
    // One formatter per language, not one per render.
    const dateFmt = useMemo(
        () => new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", { day: "numeric", month: "short", year: "numeric" }),
        [language],
    );

    useEffect(() => {
        let alive = true;
        setData(null);
        channelDocumentsForOrder(orderId).then((r) => {
            if (alive) setData(r);
        });
        return () => { alive = false; };
    }, [orderId]);

    const groups = useMemo(() => groupDocuments(data?.documents ?? []), [data]);

    if (!data || !data.enabled || (data.documents.length === 0 && data.errors.length === 0)) return null;

    const channel = data.channel_name;
    const title = channel ? t("ocd_title", { channel }) : t("ocd_title_generic");
    // Label each channel order once there is more than one in play,
    // counting the ones that failed: unlabelled documents beside "#364932
    // didn't answer" would read as covering it too.
    const references = new Set<string>([
        ...data.documents.map((d) => d.order_reference),
        ...data.errors.map((e) => e.order_reference),
    ]);
    const labelled = references.size > 1;

    const openDoc = async (number: string) => {
        const r = await openProtectedFile(channelDocumentPdfPath(orderId, number));
        if (r.notAvailable) toast.info(t("ocd_not_available"));
        else if (!r.ok) toast.error(t("ocd_file_failed"));
    };

    const when = (iso: string | null) => {
        if (!iso) return "";
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? "" : dateFmt.format(d);
    };

    const button = (d: ChannelDocument) => {
        const amount = money(d.amount_minor, d.currency);
        const name = t(d.type === "invoice" ? "ocd_invoice" : "ocd_credit_note", { number: d.number });
        const date = when(d.issued_at);
        return (
            <button
                key={`${d.type}:${d.number}`}
                type="button"
                className="btn btn-sm rt-btn btn-ghost"
                title={d.type === "credit_note" && d.invoice_number ? t("ocd_corrects", { number: d.invoice_number }) : undefined}
                onClick={() => openDoc(d.number)}
            >
                <FileText size={14} /> {amount ? `${name} · ${amount}` : name}
                {date && <span className="rt-muted"> · {date}</span>}
            </button>
        );
    };

    return (
        <section className="rt-order-panel" aria-label={title} data-testid="channel-documents-panel">
            <h3 className="rt-h3"><FileText size={14} /> {title}</h3>
            {labelled
                ? groups.map((g) => (
                    <div key={g.reference} className="rt-order-panel-row" data-testid={`channel-documents-${g.reference}`}>
                        <span className="rt-mono rt-strong">{g.reference ? `#${g.reference}` : ""}</span>
                        <div className="rt-order-panel-invoices">{g.documents.map(button)}</div>
                    </div>
                ))
                : groups.length > 0 && <div className="rt-order-panel-invoices">{groups[0].documents.map(button)}</div>}
            {data.errors.length > 0 && (
                <div className="ocd-errors">
                    {data.errors.map((e, i) => (
                        <span key={`${e.order_reference}:${i}`} className="rt-muted rt-small" data-testid="channel-documents-error">
                            {channel
                                ? t("ocd_error", { channel, reference: e.order_reference })
                                : t("ocd_error_generic", { reference: e.order_reference })}
                        </span>
                    ))}
                </div>
            )}
        </section>
    );
};

export default OrderChannelDocuments;
